import { Vector2 } from "../../../common/vector2.js";
import { Game } from "../../../game.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { BasicEventsController } from "../../../touch_events_controller/basic_events_controller.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { GameSettingsMenu } from "../game_screen/ui/game_settings_menu.js";
import { OverlayManager } from "../../overlay/overlay_manager.js";
import { OverlayId } from "../../overlay/overlay_ids.js";
import { audioController } from "../../../audio/audio_controller.js";
import { AudioClips } from "../../../audio/audio_constants.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { formatShortDuration } from "../../../common/common_funcs.js";
import { PrizeShotService } from "../../../prize_shot/prize_shot_service.js";
import type { PrizeShotScreen } from "../../../prize_shot/prize_shot_screen.js";
import { PrizeShotPrizesTooltip } from "./prize_shot_prizes_panel.js";
import { InvalidShotMessage } from "../game_screen/ui/invalid_shot_message.js";

/**
 * HUD of the Prize Shot round on the Figma frame 1080×2340. Top elements hang
 * from the top edge, bottom ones from the bottom; across, each keeps its side.
 */
export class PrizeShotUI extends UIElement
{
  private static readonly DESIGN_W = 1080;
  private static readonly TIMER_X = 386;
  private static readonly TIMER_Y = 411;
  private static readonly TIMER_H = 94;
  private static readonly BOTTOM_BUTTON_W = 470;
  private static readonly BOTTOM_BUTTON_H = 208;
  private static readonly BOTTOM_BUTTON_BOTTOM = 390;
  private static readonly BOTTOM_BUTTON_GAP = 30;
  private static readonly BOTTOM_BUTTON_FONT_SIZE = 60;
  private static readonly TEXT_OUTLINE_COLOR = "#003f87";
  private static readonly FADE_MS = 120;

  private readonly screen: PrizeShotScreen;
  private events_controller: BasicEventsController | null = null;
  private balls_text!: UITextElement;
  private gear!: UIImage;
  private settings_menu!: GameSettingsMenu;
  private logo!: UIImage;
  private timer!: UIElementHolder;
  private timer_text!: UITextElement;
  private clock_hand!: UIImage;
  private info_button!: UIImage;
  private prizes_left_title!: UITextElement;
  private prizes_left_count!: UITextElement;
  private more_button!: UINineSliceElement;
  private exit_button!: UINineSliceElement;
  private invalid_shot_message!: InvalidShotMessage;
  private popup: PrizeShotPrizesTooltip | null = null;
  private popup_dismiss: UIElementHolder | null = null;

  private last_timer_text = "";
  private clock_second = -1;
  private prizes_left = -1;
  private service_revision = -1;
  private bottom_buttons_shown = false;
  private next_more_pulse = 0;
  private hud_hidden = false;
  private logo_hidden = false;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, screen: PrizeShotScreen )
  {
    super( ctx, x, y, width, height, false, false );

    this.screen = screen;
    this.initializeEventsController( ctx.canvas );
    this.createElements();
    this.bindScreen();
    this.service_revision = PrizeShotService.revision();

    requestAnimationFrame( () => this.invalidateSubtree() );
  }

  private createElements(): void
  {
    this.createBalls();
    this.createLogo();
    this.createTimer();
    this.createInfoButton();
    this.createPrizesLeft();
    this.createMoreButton();
    this.createExitButton();
    this.createSettings();
    this.invalid_shot_message = new InvalidShotMessage( this.rect.getW(), this.rect.getH() );
  }

  private bindScreen(): void
  {
    this.screen.onShotsLeftChanged = shots_left =>
    {
      this.balls_text.setText( String( shots_left ) );
      this.pulse( this.balls_text );
    };
    this.screen.onPopped = ( _cell, reward ) =>
    {
      this.closePopup();
      // OPT: The count changes on events, not continuously; update it now and
      // avoid rescanning the board on every animation frame.
      this.syncPrizesLeft();
      this.service_revision = PrizeShotService.revision();
      if ( reward?.grand )
        this.showGrandPrize();
    };
    this.screen.onRoundEndShown = shown => this.setHudHidden( shown );
    this.screen.onInvalidShot = event => this.showInvalidShotMessage( event.clientX, event.clientY );
    this.screen.onMiss = ( client_x, client_y ) => this.showMiss( client_x, client_y );
    this.screen.setBlockedTapHitTest( ( client_x, client_y ) => this.isBlockedTap( client_x, client_y ) );
  }

  public name(): string
  {
    return "PrizeShotUI";
  }

  /** Page y where the HUD ends and the shelf may begin. */
  public static hudBottom(): number
  {
    // Room under the timer before the shelf may start.
    return Game.safeAreaTop() + LayoutScaler.yFromDesign( PrizeShotUI.TIMER_Y + PrizeShotUI.TIMER_H + 24 );
  }

  public render(): void
  {
    this.invalid_shot_message.render();

    if ( !this.shouldBeRendered() )
      return;

    this.ctx.clearRect( 0, 0, Game.screenW(), Game.screenH() );
    this.startRender();
    this.finishRender();
    Game.markGameUIPainted();
  }

  public update( now: number ): void
  {
    this.updateBasic( now );
    this.settings_menu.tick( now );
    this.invalid_shot_message.update( now );

    this.syncTimer();
    this.syncClock();
    // OPT: Still catches QA/server/period changes, while the normal steady HUD
    // performs only an integer revision check.
    const revision = PrizeShotService.revision();
    if ( revision !== this.service_revision )
    {
      this.service_revision = revision;
      this.syncPrizesLeft();
    }
    this.syncBottomButtons( now );
    this.syncLogo();

    if ( Game.instance.setGameUICanvasHeight( Game.screenH() ) )
      this.forceMakeShouldBeRendered();
  }

  public destroy(): void
  {
    this.screen.onShotsLeftChanged = () => {};
    this.screen.onPopped = () => {};
    this.screen.onRoundEndShown = () => {};
    this.screen.onInvalidShot = () => {};
    this.screen.onMiss = () => {};

    this.invalid_shot_message.hide();
    this.events_controller?.cleanup();
    this.events_controller = null;
  }

  private createBalls(): void
  {
    const design_bg_x = 40;
    const design_bg_y = 77;
    const design_bg_w = 229;
    const design_bg_h = 216;
    const design_text_w = 200;

    const bg_w = LayoutScaler.widthFromDesign( design_bg_w );
    const text_w = LayoutScaler.widthFromDesign( design_text_w );
    const text_x = ( bg_w - text_w ) / 2;

    const bg = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_bg_x ),
      LayoutScaler.yFromDesign( design_bg_y ),
      bg_w,
      LayoutScaler.heightFromDesign( design_bg_h ),
      ImgID.UI_GameBlockBg
    );
    this.addChild( bg );

    const label = new UITextElement(
      this.ctx,
      text_x,
      LayoutScaler.yFromDesign( 10 ),
      text_w,
      LayoutScaler.heightFromDesign( 54 ),
      {
        localization_key: "BALLS",
        font_size: LayoutScaler.textSizeFromDesign( 30 ),
        font_weight: 700,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        vertical_align: MyTextVerticalAlign.Center,
        text_align: MyTextAlign.Center,
        outline_color: ColorScheme.black_transparent_30,
        outline_width: LayoutScaler.widthFromDesign( 10 ),
      }
    );
    bg.addChild( label );

    this.balls_text = new UITextElement(
      this.ctx,
      text_x,
      LayoutScaler.yFromDesign( 70 ),
      text_w,
      LayoutScaler.heightFromDesign( 109 ),
      {
        text: String( this.screen.getShotsLeft() ),
        font_size: LayoutScaler.textSizeFromDesign( 80 ),
        font_weight: 700,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        vertical_align: MyTextVerticalAlign.Center,
        text_align: MyTextAlign.Center,
        outline_color: ColorScheme.game_ui_text_outline_common,
        outline_width: LayoutScaler.widthFromDesign( 13 ),
        outline_offset_y: LayoutScaler.yFromDesign( 3 ),
        auto_wrap: false,
        scale_to_width: true,
      }
    );
    bg.addChild( this.balls_text );
  }

  private createLogo(): void
  {
    const design_logo_x = 313;
    const design_logo_y = 135;
    const design_logo_w = 463;
    const design_logo_h = 297;

    this.logo = new UIImage(
      this.ctx,
      this.fromMiddle( design_logo_x ),
      LayoutScaler.yFromDesign( design_logo_y ),
      LayoutScaler.widthFromDesign( design_logo_w ),
      LayoutScaler.heightFromDesign( design_logo_h ),
      ImgID.UI_PrizeShotLogo
    );
    this.addChild( this.logo );
  }

  private createTimer(): void
  {
    // The visible pill inside the timer box, and where it sits inside
    // timer_bg.png (135×102, with a clear margin around it).
    const design_pill_x = 12;
    const design_pill_y = 9;
    const design_pill_w = 285;
    const design_pill_h = 71;
    const art_h = 102;
    const art_left = 15;
    const art_top = 12;
    const art_bottom = 84;

    const art_scale = design_pill_h / ( art_bottom - art_top );
    const design_margin_x = art_left * art_scale;

    const bg_y = LayoutScaler.heightFromDesign( design_pill_y - art_top * art_scale );
    const bg_h = LayoutScaler.heightFromDesign( art_h * art_scale );
    // Measured from the scaled art, so the clock and the text are not a rounding px off.
    const pill_center_y = bg_y + bg_h * ( art_top + art_bottom ) / 2 / art_h;

    this.timer = new UIElementHolder(
      this.ctx,
      this.fromMiddle( PrizeShotUI.TIMER_X ),
      LayoutScaler.yFromDesign( PrizeShotUI.TIMER_Y ),
      LayoutScaler.widthFromDesign( 309 ),
      LayoutScaler.heightFromDesign( PrizeShotUI.TIMER_H )
    );
    this.addChild( this.timer );

    const bg = new UINineSliceElement(
      this.ctx,
      LayoutScaler.widthFromDesign( design_pill_x - design_margin_x ),
      bg_y,
      LayoutScaler.widthFromDesign( design_pill_w + design_margin_x * 2 ),
      bg_h,
      ImgID.UI_PrizeShotTimerBg,
      "prize_shot_timer_bg"
    );
    this.timer.addChild( bg );

    this.createClock( pill_center_y );
    this.createTimerText( pill_center_y, LayoutScaler.heightFromDesign( design_pill_h ) );
    this.syncTimer();
  }

  private createClock( center_y: number ): void
  {
    const design_clock_center_x = 49;
    const design_clock_size = 50;
    const hand_aspect = 24 / 73;

    const clock_size = LayoutScaler.widthFromDesign( design_clock_size );
    const hand_h = clock_size * 0.8;
    const hand_w = hand_h * hand_aspect;

    const clock = new UIImage(
      this.ctx,
      LayoutScaler.widthFromDesign( design_clock_center_x ) - clock_size / 2,
      center_y - clock_size / 2,
      clock_size,
      clock_size,
      ImgID.UI_ClockFrame
    );
    this.timer.addChild( clock );

    this.clock_hand = new UIImage(
      this.ctx,
      ( clock_size - hand_w ) / 2,
      ( clock_size - hand_h ) / 2,
      hand_w,
      hand_h,
      ImgID.UI_ClockHand
    );
    this.clock_hand.enableRotation();
    clock.addChild( this.clock_hand );
  }

  private createTimerText( center_y: number, text_h: number ): void
  {
    const design_text_x = 464;
    const design_text_w = 210;

    this.timer_text = new UITextElement(
      this.ctx,
      LayoutScaler.widthFromDesign( design_text_x - PrizeShotUI.TIMER_X ),
      center_y - text_h / 2,
      LayoutScaler.widthFromDesign( design_text_w ),
      text_h,
      {
        text: "",
        font_size: LayoutScaler.textSizeFromDesign( 45 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 28 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
      }
    );
    this.timer.addChild( this.timer_text );
  }

  private createInfoButton(): void
  {
    // Reuse daily_info.png. Its visible 110 px button sits in a 147 px canvas;
    // keep the same centre and visible size as the old 220-in-270 copy.
    const design_btn_x = 884;
    const design_btn_bottom = 212;
    const design_btn_size = 147;

    const size = LayoutScaler.widthFromDesign( design_btn_size );

    this.info_button = new UIImage(
      this.ctx,
      this.fromRight( design_btn_x ),
      this.fromBottom( design_btn_bottom ),
      size,
      size,
      ImgID.UI_DailyTasksScreenInfo,
      {},
      true
    );
    this.info_button.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      if ( this.popup )
        this.closePopup();
      else
        this.openPopup();
    } );
    this.addChild( this.info_button );
  }

  private createPrizesLeft(): void
  {
    const design_title_w = 900;
    const design_title_h = 80;
    const design_title_bottom = 640;
    const design_count_w = 400;
    const design_count_h = 130;
    const design_count_bottom = 565;

    const title_w = LayoutScaler.widthFromDesign( design_title_w );
    const count_w = LayoutScaler.widthFromDesign( design_count_w );

    this.prizes_left_title = new UITextElement(
      this.ctx,
      this.rect.getW2() - title_w / 2,
      this.fromBottom( design_title_bottom ),
      title_w,
      LayoutScaler.heightFromDesign( design_title_h ),
      {
        localization_key: "PRIZE_SHOT.PRIZES_LEFT_TITLE",
        font_size: LayoutScaler.textSizeFromDesign( 52 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_color: PrizeShotUI.TEXT_OUTLINE_COLOR,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        auto_wrap: false,
        scale_to_width: true,
      }
    );
    this.prizes_left_title.setClickable( false );
    this.addChild( this.prizes_left_title );

    this.prizes_left_count = new UITextElement(
      this.ctx,
      this.rect.getW2() - count_w / 2,
      this.fromBottom( design_count_bottom ),
      count_w,
      LayoutScaler.heightFromDesign( design_count_h ),
      {
        text: "",
        font_size: LayoutScaler.textSizeFromDesign( 104 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_color: PrizeShotUI.TEXT_OUTLINE_COLOR,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 16 ),
        auto_wrap: false,
        scale_to_width: true,
      }
    );
    this.prizes_left_count.setClickable( false );
    this.addChild( this.prizes_left_count );

    this.syncPrizesLeft();
  }

  /** Out of balls: "More balls" on the right, at the cannon's spot. */
  private createMoreButton(): void
  {
    const w = LayoutScaler.widthFromDesign( PrizeShotUI.BOTTOM_BUTTON_W );
    const h = LayoutScaler.heightFromDesign( PrizeShotUI.BOTTOM_BUTTON_H );

    this.more_button = new UINineSliceElement(
      this.ctx,
      this.rect.getW2() + LayoutScaler.widthFromDesign( PrizeShotUI.BOTTOM_BUTTON_GAP ) / 2,
      this.fromBottom( PrizeShotUI.BOTTOM_BUTTON_BOTTOM ),
      w,
      h,
      ImgID.UI_GreenFrameBtn,
      "frame_btn",
      undefined,
      undefined,
      true
    );

    const label = new UITextElement(
      this.ctx,
      0,
      0,
      w,
      h,
      {
        localization_key: "PRIZE_SHOT.MORE_BALLS",
        font_size: LayoutScaler.textSizeFromDesign( PrizeShotUI.BOTTOM_BUTTON_FONT_SIZE ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_color: "#1d6b07",
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        auto_wrap: false,
        scale_to_width: true,
      }
    );
    this.more_button.addChild( label, true );

    this.more_button.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      this.closePopup();
      this.screen.requestMoreBalls();
    } );
    this.more_button.setOpacityImmediate( 0 );
    this.more_button.setClickable( false );
    this.addChild( this.more_button );
  }

  /** Out of balls: "Exit" on the left, so the way out is not only in the gear menu. */
  private createExitButton(): void
  {
    const w = LayoutScaler.widthFromDesign( PrizeShotUI.BOTTOM_BUTTON_W );
    const h = LayoutScaler.heightFromDesign( PrizeShotUI.BOTTOM_BUTTON_H );

    this.exit_button = new UINineSliceElement(
      this.ctx,
      this.rect.getW2() - LayoutScaler.widthFromDesign( PrizeShotUI.BOTTOM_BUTTON_GAP ) / 2 - w,
      this.fromBottom( PrizeShotUI.BOTTOM_BUTTON_BOTTOM ),
      w,
      h,
      ImgID.UI_RedBtn,
      "frame_btn",
      undefined,
      undefined,
      true
    );

    const label = new UITextElement(
      this.ctx,
      0,
      0,
      w,
      h,
      {
        localization_key: "CLOSE_GAME_DIALOG.EXIT_BUTTON",
        font_size: LayoutScaler.textSizeFromDesign( PrizeShotUI.BOTTOM_BUTTON_FONT_SIZE ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_color: ColorScheme.red_btn_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_offset_y: LayoutScaler.yFromDesign( 2 ),
        auto_wrap: false,
        scale_to_width: true,
      }
    );
    this.exit_button.addChild( label, true );

    this.exit_button.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      this.closePopup();
      this.screen.onExit();
    } );
    this.exit_button.setOpacityImmediate( 0 );
    this.exit_button.setClickable( false );
    this.addChild( this.exit_button );
  }

  private createSettings(): void
  {
    const design_gear_x = 897;
    const design_gear_y = 76;
    const design_gear_size = 150;

    const size = LayoutScaler.widthFromDesign( design_gear_size );
    const x = this.fromRight( design_gear_x );
    const y = LayoutScaler.yFromDesign( design_gear_y );

    this.gear = new UIImage( this.ctx, x, y, size, size, ImgID.IconSettings, {}, true );
    this.gear.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      this.closePopup();
      this.settings_menu.toggle();
    } );
    this.addChild( this.gear );

    this.settings_menu = new GameSettingsMenu(
      this.ctx,
      this.rect.getW(),
      this.rect.getH(),
      x,
      y,
      size,
      size,
      () => this.invalidateSubtree(),
      () => this.screen.onExit()
    );
    this.addChild( this.settings_menu );
  }

  private syncTimer(): void
  {
    const text = formatShortDuration( PrizeShotService.timeLeftMs() );
    if ( text === this.last_timer_text )
      return;

    this.last_timer_text = text;
    this.timer_text.setText( text );
  }

  /** The hand jumps a sixtieth of a turn every second. */
  private syncClock(): void
  {
    const second = Math.floor( Date.now() / 1000 );
    if ( second === this.clock_second )
      return;

    this.clock_second = second;
    this.clock_hand.setRotationImmediate( ( second % 60 ) / 60 * Math.PI * 2 );
  }

  private syncPrizesLeft(): void
  {
    const left = PrizeShotService.prizesLeft();
    if ( left === this.prizes_left )
      return;

    const fewer = this.prizes_left >= 0 && left < this.prizes_left;
    this.prizes_left = left;
    this.prizes_left_count.setText( String( left ) );

    if ( fewer )
      this.pulse( this.prizes_left_count );
  }

  private syncBottomButtons( now: number ): void
  {
    const shown = this.screen.isWaitingForBalls() && !this.hud_hidden;
    if ( shown !== this.bottom_buttons_shown )
    {
      this.bottom_buttons_shown = shown;
      this.next_more_pulse = now + 600;

      for ( const button of [ this.more_button, this.exit_button ] )
      {
        button.setClickable( shown );
        if ( shown )
          button.showElement( 160 );
        else
          button.hideElement( PrizeShotUI.FADE_MS );
      }
    }

    if ( shown && now >= this.next_more_pulse )
    {
      this.next_more_pulse = now + 2200;
      this.pulse( this.more_button, 1.07, 220, 260 );
    }
  }

  /** The rewards overlay brings its own logo. */
  private syncLogo(): void
  {
    const hidden = OverlayManager.isOverlayOpen( OverlayId.PRIZE_SHOT_REWARDS_OVERLAY );
    if ( hidden === this.logo_hidden )
      return;

    this.logo_hidden = hidden;
    if ( hidden )
      this.logo.hideElement( PrizeShotUI.FADE_MS );
    else
      this.logo.showElement( PrizeShotUI.FADE_MS );
  }

  /** While the end-of-round overlays are up only the logo stays. */
  private setHudHidden( hidden: boolean ): void
  {
    if ( hidden === this.hud_hidden )
      return;

    this.hud_hidden = hidden;
    this.closePopup();
    this.settings_menu.close();

    for ( const button of [ this.gear, this.info_button ] )
      button.setClickable( !hidden );

    for ( const element of [ this.gear, this.info_button, this.timer, this.prizes_left_title, this.prizes_left_count ] )
    {
      if ( hidden )
        element.hideElement( PrizeShotUI.FADE_MS );
      else
        element.showElement( PrizeShotUI.FADE_MS );
    }
  }

  private openPopup(): void
  {
    const round = PrizeShotService.round();
    if ( !round || this.popup )
      return;

    // A tap anywhere closes the panel and is not a shot.
    this.popup_dismiss = new UIElementHolder( this.ctx, 0, 0, this.rect.getW(), this.rect.getH() );
    this.popup_dismiss.setOnClick( () => this.closePopup() );
    this.addChild( this.popup_dismiss );

    const pos = this.info_button.pixelPosImportant();
    const rect = this.info_button.getRect();
    const anchor = { x: pos.x, y: pos.y, w: rect.getW(), h: rect.getH() };

    this.popup = PrizeShotPrizesTooltip.createAt( this.ctx, round, anchor, this.rect.getW() );
    this.popup.setOnClick( () => this.closePopup() );
    this.addChild( this.popup );

    this.invalidateSubtree();
  }

  private closePopup(): void
  {
    if ( this.popup )
      this.removeChild( this.popup );
    if ( this.popup_dismiss )
      this.removeChild( this.popup_dismiss );

    this.popup = null;
    this.popup_dismiss = null;
    this.invalidateSubtree();
  }

  /** "Miss!" where the ball struck, floating up as it fades. */
  private showMiss( client_x: number, client_y: number ): void
  {
    const design_text_w = 420;
    const design_text_h = 110;
    const design_rise = 90;
    const duration_ms = 900;
    const fade_from = 0.35;

    const text_w = LayoutScaler.widthFromDesign( design_text_w );
    const text_h = LayoutScaler.heightFromDesign( design_text_h );
    const { x, y } = this.clientToLocal( client_x, client_y );
    const text_x = Math.min( Math.max( x - text_w / 2, 0 ), this.rect.getW() - text_w );
    const text_y = y - text_h / 2;

    const text = new UITextElement(
      this.ctx,
      text_x,
      text_y,
      text_w,
      text_h,
      {
        localization_key: "PRIZE_SHOT.MISS",
        font_size: LayoutScaler.textSizeFromDesign( 64 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_color: "#9e1020",
        outline_width: LayoutScaler.textBorderSizeFromDesign( 12 ),
        auto_wrap: false,
        scale_to_width: true,
      }
    );
    text.setClickable( false );
    this.addChild( text );

    const now = performance.now();
    const target = new Vector2( text_x, text_y - LayoutScaler.heightFromDesign( design_rise ) );

    text.setOpacityImmediate( 0 );
    text.fadeIn( 90, now, Easing.EaseOutQuad );
    text.fadeOut( duration_ms * ( 1 - fade_from ), now + duration_ms * fade_from, Easing.EaseInQuad );
    text.addTransform( new TransformStraightMove( text, true, null, target, now, duration_ms, { easing: Easing.EaseOutQuad } ) );

    this.removeLater( text, duration_ms + 60 );
  }

  /** "Grand Prize!" bursts out over the shelf, holds, and goes. */
  private showGrandPrize(): void
  {
    const design_banner_y = 1020;
    const design_banner_w = 900;
    const design_banner_h = 180;

    const banner_w = LayoutScaler.widthFromDesign( design_banner_w );

    const banner = new UITextElement(
      this.ctx,
      this.rect.getW2() - banner_w / 2,
      LayoutScaler.yFromDesign( design_banner_y ),
      banner_w,
      LayoutScaler.heightFromDesign( design_banner_h ),
      {
        localization_key: "GRAND_PRIZE",
        font_size: LayoutScaler.textSizeFromDesign( 80 ),
        font_weight: 900,
        text_color: "#ffd84a",
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_color: "#8a3a00",
        outline_width: LayoutScaler.textBorderSizeFromDesign( 16 ),
        shadow_color: "rgba(0, 0, 0, 0.45)",
        shadow_blur: LayoutScaler.widthFromDesign( 10 ),
        shadow_offset_x: 0,
        shadow_offset_y: LayoutScaler.heightFromDesign( 6 ),
        auto_wrap: false,
        scale_to_width: true,
      }
    );
    banner.setClickable( false );
    this.addChild( banner );

    const now = performance.now();
    const scale = ( s: number ) => new Vector2( s, s );

    banner.setScaleImmediate( scale( 0.2 ) );
    banner.setOpacityImmediate( 0 );
    banner.fadeIn( 120, now, Easing.EaseOutQuad );
    banner.addTransform( new TransformScale( banner, true, scale( 0.2 ), scale( 1.25 ), now, 260, { easing: Easing.EaseOutQuad } ) );
    banner.addTransform( new TransformScale( banner, true, scale( 1.25 ), scale( 1 ), now + 260, 160, { easing: Easing.EaseInQuad } ) );
    banner.addTransform( new TransformScale( banner, true, scale( 1 ), scale( 1.15 ), now + 1500, 320, { easing: Easing.EaseInQuad } ) );
    banner.fadeOut( 320, now + 1500, Easing.EaseInQuad );

    this.removeLater( banner, 1900 );
    void audioController.playSound( AudioClips.WIN_GAME );
  }

  /** Placed as in a level: over the tap, clear of the edges and the cannon. */
  private showInvalidShotMessage( client_x: number, client_y: number ): void
  {
    const width = Math.min( LayoutScaler.widthFromDesign( 760 ), this.rect.getW() - LayoutScaler.widthFromDesign( 40 ) );
    const height = LayoutScaler.heightFromDesign( 110 );
    const padding = LayoutScaler.widthFromDesign( 20 );
    const bottom_reserved = LayoutScaler.heightFromDesign( 360 );
    const { x, y } = this.clientToLocal( client_x, client_y );

    const max_x = this.rect.getW() - width - padding;
    const max_y = Math.max( padding, this.rect.getH() - height - bottom_reserved );
    const text_x = Math.round( Math.min( Math.max( x - width / 2, padding ), max_x ) );
    const text_y = Math.round( Math.min( Math.max( y - height / 2, padding ), max_y ) );

    this.invalid_shot_message.show( text_x, text_y, width, height );
  }

  private removeLater( element: UIElement, delay_ms: number ): void
  {
    this.invalidateSubtree();
    window.setTimeout( () =>
    {
      this.removeChild( element );
      this.invalidateSubtree();
    }, delay_ms );
  }

  private pulse( element: UIElement, peak = 1.25, grow_ms = 110, shrink_ms = 140 ): void
  {
    const now = performance.now();
    const rest = new Vector2( 1, 1 );
    const top = new Vector2( peak, peak );

    element.addTransform( new TransformScale( element, true, rest, top, now, grow_ms, { easing: Easing.EaseOutQuad } ) );
    element.addTransform( new TransformScale( element, true, top, rest, now + grow_ms, shrink_ms, { easing: Easing.EaseInQuad } ) );
  }

  /** Taps on HUD controls and on the open panel never fire the cannon. */
  private isBlockedTap( client_x: number, client_y: number ): boolean
  {
    if ( this.settings_menu.isBusy() || this.popup )
      return true;

    const canvas_rect = this.ctx.canvas.getBoundingClientRect();
    const x = client_x - canvas_rect.left;
    const y = client_y - canvas_rect.top;

    const buttons: UIElement[] = [ this.gear, this.info_button ];
    if ( this.bottom_buttons_shown )
      buttons.push( this.more_button, this.exit_button );

    return buttons.some( button =>
    {
      const r = button.getAbsoluteRect();
      return x >= r.x && x <= r.x + r.width && y >= r.y && y <= r.y + r.height;
    } );
  }

  /** Page point (client px) in this HUD's px. */
  private clientToLocal( client_x: number, client_y: number ): { x: number; y: number }
  {
    const canvas_rect = this.ctx.canvas.getBoundingClientRect();

    return {
      x: ( client_x - canvas_rect.left ) / canvas_rect.width * Game.screenW() - Game.safeAreaLeft(),
      y: ( client_y - canvas_rect.top ) / canvas_rect.height * Game.screenH() - Game.safeAreaTop(),
    };
  }

  private fromBottom( design_from_bottom: number ): number
  {
    return this.rect.getH() - LayoutScaler.heightFromDesign( design_from_bottom );
  }

  /** Keeps its distance to the right edge. */
  private fromRight( design_x: number ): number
  {
    return this.rect.getW() - LayoutScaler.widthFromDesign( PrizeShotUI.DESIGN_W - design_x );
  }

  /** Keeps its distance to the centre line. */
  private fromMiddle( design_x: number ): number
  {
    return this.rect.getW2() + LayoutScaler.widthFromDesign( design_x - PrizeShotUI.DESIGN_W / 2 );
  }

  private initializeEventsController( canvas: HTMLCanvasElement ): void
  {
    this.events_controller = new BasicEventsController( canvas );
    this.events_controller.init(
      ( p: Vector2 ) =>
      {
        if ( OverlayManager.isInteractionBlocked() )
          return;
        super.onTouchStart( p, performance.now() );
      },
      ( p: Vector2 ) =>
      {
        if ( OverlayManager.isInteractionBlocked() )
          return;
        super.onTouchEnd( p, performance.now() );
      },
      ( p: Vector2 ) =>
      {
        if ( OverlayManager.isInteractionBlocked() )
          return;
        super.onTouchMove( p, performance.now() );
      }
    );
  }
}
